import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-entwickler";
const TITLE = "Shopify Entwickler: Aufgaben, Kosten, Zusammenarbeit";
const DESCRIPTION =
  "Was ein Shopify Entwickler macht, was Theme-Anpassungen kosten, wann eine App ersetzt werden sollte, und wie Entwicklung und Conversion-Optimierung zusammenspielen.";

const faqs = [
  {
    q: "Was macht ein Shopify Entwickler?",
    a: "Er arbeitet im Theme-Code (Liquid, JavaScript, CSS), baut Sektionen und Funktionen, ersetzt Apps durch native Lösungen, richtet Tracking ein und sorgt dafür, dass der Store schnell bleibt.",
  },
  {
    q: "Was kostet ein Shopify Entwickler?",
    a: "80 bis 150 € netto pro Stunde. Eine einzelne Theme-Anpassung liegt meist zwischen 300 und 1.500 €, ein vollständiger Conversion-Umbau von Produktseite, Warenkorb und Checkout-Vorstufe ab etwa 3.990 € netto.",
  },
  {
    q: "Brauche ich einen Entwickler oder reicht eine App?",
    a: "Apps sind schnell, kosten aber monatlich und laden zusätzliche Skripte. Ab drei bis vier Apps mit ähnlichem Zweck ist eine native Theme-Lösung meist günstiger und deutlich schneller.",
  },
  {
    q: "Wird mein Theme durch Anpassungen updatefähig bleiben?",
    a: "Ja, wenn sauber gearbeitet wird: eigene Sektionen und Snippets statt Änderungen in Kerndateien, dokumentierte Eingriffe und ein Duplikat des Themes als Arbeitskopie vor jedem Livegang.",
  },
  {
    q: "Wie läuft die Zusammenarbeit ab?",
    a: "Arbeitskopie des Themes anlegen, Umfang schriftlich festhalten, in Etappen umsetzen, jede Etappe im Vorschau-Theme abnehmen, danach live schalten und die Kennzahlen zwei Wochen beobachten.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-entwickler")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Entwicklung"
      title="Shopify Entwickler: was im Theme wirklich passieren muss"
      intro="Entwicklung ist kein Selbstzweck. Sie zahlt sich dann aus, wenn sie Ladezeit senkt, Apps ersetzt und Kaufentscheidungen erleichtert. Hier steht, welche Arbeiten sich lohnen und was sie kosten."
      source="ratgeber-shopify-entwickler"
      sections={[
        {
          h: "Typische Aufgaben",
          p: [],
          list: [
            "Produktseiten-Sektionen: Varianten, Bundles, Lieferzeit, Vertrauenselemente",
            "Warenkorb und Checkout-Vorstufe: Versandschwelle, Zahlarten, Zusatzverkäufe",
            "Ladezeit: Bilder, Fonts, Skripte, App-Reste entfernen",
            "Tracking: sauberes Datenmodell für Kaufabschlüsse und Warenkorb-Ereignisse",
            "Schnittstellen: ERP, Versand, Abo- oder Konfigurator-Logik",
          ],
        },
        {
          h: "Apps ersetzen statt stapeln",
          p: [
            "Jede App bringt eigene Skripte mit. In den Stores, die ich übernehme, finde ich regelmäßig fünf bis zehn aktive Apps, von denen zwei bis drei denselben Zweck erfüllen.",
            "Eine App-Inventur senkt typischerweise die laufenden Kosten um 100 bis 200 € pro Monat und die Ladezeit um 0,3 bis 0,8 Sekunden, letzteres wirkt direkt auf die mobile Kaufabschlussrate.",
          ],
        },
        {
          h: "Preise nach Umfang",
          p: [],
          list: [
            "Einzelne Sektion oder Anpassung: 300 bis 1.500 €",
            "Produktseite komplett neu strukturiert: 1.500 bis 4.000 €",
            "Conversion-Sprint über den ganzen Kauftrichter: ab 3.990 € netto",
            "Theme-Neubau: 8.000 bis 25.000 € je nach Sortiment",
          ],
        },
        {
          h: "Sauber arbeiten heißt: rückbaubar arbeiten",
          p: [
            "Vor jedem Eingriff entsteht eine Kopie des Live-Themes. Änderungen liegen in eigenen Sektionen und Snippets, nicht in Kerndateien. So bleibt das Theme updatefähig und jeder Schritt lässt sich innerhalb von Minuten zurücknehmen.",
          ],
        },
        {
          h: "Entwicklung und Conversion gehören zusammen",
          p: [
            "Code ohne Diagnose baut Funktionen, die niemand vermisst. Deshalb steht am Anfang die Messung: Wo bricht der Trichter ein, mobil und am Desktop? Erst danach wird entschieden, was gebaut wird.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-pagespeed-optimieren", label: "Pagespeed optimieren" },
        { to: "/leistungen", label: "Leistungen" },
        { to: "/kontakt", label: "Projekt anfragen" },
      ]}
    />
  );
}
